import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Store, Package, ArrowLeft, Minus, Plus, Loader, ShoppingCart } from 'lucide-react';
import { Navbar } from '../components/Navbar';
import { useAuth } from '../contexts/AuthContext';
import { shopApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const CreatorStorePage = () => {
  const { companyId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [storefront, setStorefront] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [selectedVariants, setSelectedVariants] = useState({});
  const [addingToCart, setAddingToCart] = useState(false);

  useEffect(() => {
    shopApi.getStorefront(companyId).then(d => { setStorefront(d); setLoading(false); }).catch(() => setLoading(false));
  }, [companyId]);

  const openProduct = (product) => {
    setSelectedProduct(product);
    setQuantity(1);
    const defaults = {};
    (product.variants || []).forEach(g => { if (g.values?.length) defaults[g.name] = g.values[0]; });
    setSelectedVariants(defaults);
  };

  const handleAddToCart = async () => {
    if (!user) { toast.error('Please login first'); navigate('/login'); return; }
    setAddingToCart(true);
    try {
      await shopApi.addToCart(selectedProduct.product_id, quantity, selectedVariants);
      toast.success('Added to cart!');
      window.dispatchEvent(new Event('cart-updated'));
      setSelectedProduct(null);
    } catch (e) { toast.error(e.message || 'Failed to add'); }
    setAddingToCart(false);
  };

  if (loading) return <div className="min-h-screen bg-[#030014]"><Navbar /><div className="flex items-center justify-center h-[60vh]"><Loader className="w-8 h-8 animate-spin text-[#8B5CF6]" /></div></div>;
  if (!storefront) return <div className="min-h-screen bg-[#030014]"><Navbar /><div className="flex flex-col items-center justify-center h-[60vh]"><Store className="w-16 h-16 text-gray-600 mb-4" /><h2 className="text-xl font-semibold">Store Not Found</h2><Button onClick={() => navigate('/')} className="mt-6"><ArrowLeft className="w-4 h-4 mr-2" /> Back</Button></div></div>;

  const { creator, products } = storefront;

  return (
    <div className="min-h-screen bg-[#030014]">
      <Navbar />
      <main className="max-w-6xl mx-auto px-4 pt-24 pb-16">
        <button onClick={() => navigate(-1)} className="text-gray-400 hover:text-white mb-4 flex items-center gap-2 text-sm">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>

        {/* Store Header */}
        <div className="admin-card p-6 flex items-center gap-6 mb-8">
          <div className="w-16 h-16 rounded-full bg-[#8B5CF6]/20 flex items-center justify-center flex-shrink-0">
            {creator.logo ? <img src={creator.logo} alt="" className="w-full h-full rounded-full object-cover" /> : <Store className="w-8 h-8 text-[#8B5CF6]" />}
          </div>
          <div>
            <h1 className="text-2xl font-bold">{creator.name}'s Shop</h1>
            {creator.description && <p className="text-gray-400 mt-1 text-sm">{creator.description}</p>}
          </div>
        </div>

        {/* Product Grid */}
        {products.length === 0 ? (
          <div className="text-center py-16 admin-card"><Package className="w-12 h-12 mx-auto text-gray-600 mb-3" /><p className="text-gray-400">No products available</p></div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {products.map(product => (
              <div key={product.product_id} onClick={() => openProduct(product)} className="admin-card overflow-hidden cursor-pointer hover:border-[#8B5CF6]/30 transition-colors group" data-testid={`product-card-${product.product_id}`}>
                {product.images?.[0] ? (
                  <img src={product.images[0]} alt={product.name} className="w-full h-48 object-cover" />
                ) : (
                  <div className="w-full h-48 bg-[#8B5CF6]/5 flex items-center justify-center"><Package className="w-12 h-12 text-gray-600" /></div>
                )}
                <div className="p-4">
                  <h3 className="font-semibold group-hover:text-[#8B5CF6] transition-colors">{product.name}</h3>
                  <p className="text-gray-400 text-sm line-clamp-2 mt-1">{product.description}</p>
                  <div className="flex items-center justify-between mt-3">
                    <span className="text-lg font-bold text-[#8B5CF6]">${product.price.toFixed(2)}</span>
                    {product.variants?.length > 0 && <span className="text-xs text-gray-500">{product.variants.length} option{product.variants.length > 1 ? 's' : ''}</span>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Product Detail Modal */}
        {selectedProduct && (
          <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setSelectedProduct(null)}>
            <div className="bg-[#1a1035] border border-[#8B5CF6]/20 rounded-xl w-full max-w-lg max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="product-detail-modal">
              {selectedProduct.images?.[0] && (
                <img src={selectedProduct.images[0]} alt="" className="w-full h-56 object-cover rounded-t-xl" />
              )}
              <div className="p-6 space-y-4">
                <div>
                  <h2 className="text-xl font-bold">{selectedProduct.name}</h2>
                  <p className="text-2xl font-bold text-[#8B5CF6] mt-1">${selectedProduct.price.toFixed(2)}</p>
                  <p className="text-gray-400 text-sm mt-2">{selectedProduct.description}</p>
                </div>

                {/* Variant Selectors */}
                {(selectedProduct.variants || []).map((group) => (
                  <div key={group.name} data-testid={`variant-group-${group.name}`}>
                    <label className="text-xs text-gray-400 mb-1.5 block font-medium">{group.name}</label>
                    <select
                      value={selectedVariants[group.name] || ''}
                      onChange={e => setSelectedVariants(p => ({ ...p, [group.name]: e.target.value }))}
                      className="w-full px-3 py-2.5 rounded-lg bg-white/5 border border-white/10 text-sm focus:border-[#8B5CF6] outline-none appearance-none"
                      data-testid={`variant-select-${group.name}`}
                    >
                      {group.values.map(v => <option key={v} value={v}>{v}</option>)}
                    </select>
                  </div>
                ))}

                {/* Quantity */}
                <div>
                  <label className="text-xs text-gray-400 mb-1.5 block font-medium">Quantity</label>
                  <div className="flex items-center gap-3">
                    <button onClick={() => setQuantity(q => Math.max(1, q - 1))} className="w-9 h-9 rounded-lg bg-white/5 flex items-center justify-center hover:bg-white/10"><Minus className="w-4 h-4" /></button>
                    <span className="font-semibold w-8 text-center">{quantity}</span>
                    <button onClick={() => setQuantity(q => q + 1)} className="w-9 h-9 rounded-lg bg-white/5 flex items-center justify-center hover:bg-white/10"><Plus className="w-4 h-4" /></button>
                  </div>
                </div>

                <Button onClick={handleAddToCart} disabled={addingToCart} className="w-full py-3 bg-[#8B5CF6] hover:bg-[#7C3AED] text-white" data-testid="add-to-cart-btn">
                  <ShoppingCart className="w-4 h-4 mr-2" />
                  {addingToCart ? 'Adding...' : `Add to Cart — $${(selectedProduct.price * quantity).toFixed(2)}`}
                </Button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default CreatorStorePage;
